/** @jsx jsx */
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { CircularProgress } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ICustomTheme } from 'interfaces';

/**
 * Delays (ms) before the intermediate reassurance messages appear.
 * Chosen to leave time to read each step (~3s per message).
 */
const STEP_PREPARING_DELAY_MS = 2000;
const STEP_ALMOST_DONE_DELAY_MS = 5000;
const STEP_KEYS = [
  null,
  'core.submitting.step_preparing',
  'core.submitting.step_almost_done',
] as const;

const Backdrop = styled.div<{ theme: ICustomTheme }>`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => (theme.zIndex?.modal ?? 1300) + 10};
  background-color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing(3)}px`};
`;

const Card = styled.div<{ theme: ICustomTheme }>`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-radius: 12px;
  padding: ${({ theme }) => `${theme.spacing(4)}px ${theme.spacing(5)}px`};
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)}px`};
`;

const Title = styled.h2<{ theme: ICustomTheme }>`
  margin: 0;
  font-size: 1.6rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const Step = styled.p<{ theme: ICustomTheme }>`
  margin: 0;
  font-size: 1.3rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  min-height: 1.5em;
  transition: opacity 0.25s ease-in-out;
`;

const StyledSpinner = styled(CircularProgress)<{ theme: ICustomTheme }>(
  ({ theme }) => ({
    color: theme.colors.paletteFromBO.main,
  }),
);

interface SubmittingOverlayProps {
  title: string;
  /** Success state: no spinner nor steps, `successMessage` replaces the title. */
  isSuccess?: boolean;
  successMessage?: string;
}

/**
 * Full-screen overlay shown while the submit request is pending.
 * Blocks interactions, shows progressive reassurance messages,
 * then a success message before the redirect.
 */
const SubmittingOverlay = ({
  title,
  isSuccess,
  successMessage,
}: SubmittingOverlayProps): JSX.Element => {
  const { t } = useTranslation();
  const [step, setStep] = useState<0 | 1 | 2>(0);

  useEffect(() => {
    if (isSuccess) {
      return undefined;
    }
    const t1 = window.setTimeout(() => setStep(1), STEP_PREPARING_DELAY_MS);
    const t2 = window.setTimeout(() => setStep(2), STEP_ALMOST_DONE_DELAY_MS);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isSuccess]);

  const stepKey = STEP_KEYS[step];

  return (
    <Backdrop
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      aria-busy={!isSuccess}
    >
      <Card>
        {!isSuccess && <StyledSpinner size={48} />}
        <Title>{isSuccess ? successMessage : title}</Title>
        {!isSuccess && <Step>{stepKey ? t(stepKey) : '\u00A0'}</Step>}
      </Card>
    </Backdrop>
  );
};

SubmittingOverlay.defaultProps = {
  isSuccess: false,
  successMessage: undefined,
};

export default SubmittingOverlay;
